import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import * as signalR from "@microsoft/signalr";

import { GlobalOperations } from '../../Controller/Common/GlobalOperations';
import { StartNewSessionResponse } from '../../Model/Response/SignalR/StartNewSessionResponse';
import { SendMessageByClientUserResponse } from '../../Model/Response/SignalR/SendMessageByClientUserResponse';
import { GetChatSessionDetailResponse } from '../../Model/Response/ClientAgent/GetChatSessionDetailResponse';
import { EndSessionByAgentResponse } from '../../Model/Response/HubResponses/EndSessionByAgentResponse';
import { ChangeAgentTypingRequest } from '../../Model/Request/SignalR/ChangeAgentTypingRequest';
import { ChangeAgentTypingResponse } from '../../Model/Response/SignalR/ChangeAgentTypingResponse';
import { DisplayType, StatusEnum, useShowStatusMessage } from '../Components/CoreComponents/StatusMessage';

// SessionManagement modeli
export interface SessionManagement {
    sessionId: number;
    clientCompanyName: string;
    clientId: number;
    serviceId: number;
    hasNewMessage: boolean;
}

// Mesajları temsil eden model
export interface AgentSideMessages {
    messageContent: string;
    messageOwnerName: string;
    messageDateUtc: number;
    messageSide: MessageSideEnum;
}

// Mesajın hangi taraftan geldiğini belirten enum
export enum MessageSideEnum {
    ClientUser = 1,
    Agent = 2
}

// ChatContext'in tipi
interface ChatContextType {
    messages: AgentSideMessages[];
    setMessages: (messages: AgentSideMessages[]) => void;
    addMessage: (message: AgentSideMessages) => void;
    startSignalR: () => Promise<void>;
    activeSessions: SessionManagement[];
    setActiveSessions: (sessions: SessionManagement[]) => void;
    currentSession: GetChatSessionDetailResponse | null; // null olabilir
    setCurrentSession: (session: GetChatSessionDetailResponse | null) => void; // null olabilir
    removeSessionById: (sessionId: number) => void; // yeni eklenen fonksiyon

    typingUsers: { [sessionId: number]: string[] }; // Oturum bazında yazan kullanıcıların listesi
    setTypingUsers: (sessionId: number, userName: string, isTyping: boolean) => void; // userId parametresini ekliyoruz


    setGlobalInputValue: (value: string) => void;
    checkBeforeSwitchingSession: () => boolean;
}


// ChatContext oluşturuluyor
const ChatContext = createContext<ChatContextType | undefined>(undefined);






export const ChatProvider: React.FC<{ children: ReactNode }> = ({ children }) => {

    const [messages, setMessages] = useState<AgentSideMessages[]>([]);
    const [connection, setConnection] = useState<signalR.HubConnection | null>(null);
    const [activeSessions, setActiveSessions] = useState<SessionManagement[]>([]);
    const [currentSession, setCurrentSession] = useState<GetChatSessionDetailResponse | null>(null);
    const [typingUsers, setTypingUsersState] = useState<{ [sessionId: number]: string[] }>({});

    // Yeni mesaj ekleme fonksiyonu
    const addMessage = (message: AgentSideMessages) => {
        setMessages(prevMessages => [...prevMessages, message]);
    };

    // ChatContext'in içine oturum silme fonksiyonu ekliyoruz
    const removeSessionById = (sessionId: number) => {
        setActiveSessions((prevSessions: SessionManagement[]) =>
            prevSessions.filter(session => session.sessionId !== sessionId)
        );
    };

    //typeing işlemini yapan kullanıcı id ve adı buraya gelir
    const setTypingUsers = async (sessionId: number, userName: string, isTyping: boolean) => {

        if (GlobalOperations.SignalRConnection == null)
            return;

        // Mevcut yazma durumu varsa kontrol et
        const currentTypingUsers = typingUsers[sessionId] || [];

        const isUserTyping = currentTypingUsers.includes(userName);

        // Eğer durum aynı ise herhangi bir işlem yapma
        if (isTyping === isUserTyping) {
            return; // Durum değişmediyse çık
        }

        let request: ChangeAgentTypingRequest = {
            agentName: userName,
            auth: GlobalOperations.UserToken,
            isTyping: isTyping,
            sessionId: sessionId
        }

        let response: boolean = await GlobalOperations.SignalRConnection.invoke("changeIsAgentTyping", request);



    };

    const [inputValue, setGlobalInputValue] = useState('');


    const checkBeforeSwitchingSession = (): boolean => {
        if (inputValue.trim()) {
            return false;
        }
        return true;
    };

    // SignalR bağlantısını başlatan fonksiyon
    const startSignalR = async () => {
        if (GlobalOperations.SignalRConnection) {
            console.log("SignalR zaten başlatıldı");
            return;
        }

        try {
            const newConnection = new signalR.HubConnectionBuilder()
                .withUrl(GlobalOperations.SignalRBaseUrl + "/signalr", {
                    transport: signalR.HttpTransportType.WebSockets
                })
                .configureLogging(signalR.LogLevel.Information)
                .withAutomaticReconnect([0, 2000, 10000, 30000]) // Bağlantı koparsa yeniden bağlanma denemesi yapar
                .build();

            setConnection(newConnection);

            // Bağlantı durumlarını dinleyelim
            newConnection.onreconnecting((error) => {
                console.warn("SignalR yeniden bağlanmaya çalışıyor...", error);
                // Burada bir UI gösterimi yapılabilir
            });

            newConnection.onreconnected((connectionId) => {
                console.log("SignalR yeniden bağlandı. Bağlantı ID'si:", connectionId);
                // UI'da bağlantının yeniden sağlandığına dair bilgi gösterebilirsiniz
            });

            newConnection.onclose((error) => {
                console.error("SignalR bağlantısı kapandı:", error);
                // Kullanıcıya bağlantının koptuğunu belirtmek için bir hata mesajı gösterebilirsiniz
            });

            newConnection.onclose(async () => {
                console.log("Connection lost. Attempting to reconnect...");
                try {
                    await newConnection.start();
                    const id = await newConnection.invoke<string>("GetConnectionId");
                    GlobalOperations.ConnectionHubId = id;
                    console.log("Reconnected!");
                } catch (err) {
                    console.error("Failed to reconnect:", err);
                }
            });
            

            await newConnection.start();
            console.log("SignalR bağlantısı kuruldu");

            // Bağlantı ID'sini al
            const id = await newConnection.invoke<string>("GetConnectionId");
            GlobalOperations.ConnectionHubId = id;
            GlobalOperations.SignalRConnection = newConnection;

            // Yeni oturum başladığında çağrılır
            newConnection.on('newSessionStart', (session: StartNewSessionResponse) => {
                if (!session) return;

                const newSession: SessionManagement = {
                    sessionId: Number(session.sessionId),
                    clientCompanyName: session.clientName || '',
                    clientId: Number(session.clientId),
                    serviceId: Number(session.serviceId),
                    hasNewMessage: true
                };


                setActiveSessions((prevSessions) => {
                    // Oturum listesinde zaten var mı kontrol et
                    const sessionExists = prevSessions.some(existingSession => existingSession.sessionId === newSession.sessionId);

                    if (sessionExists) {
                        // Zaten varsa, sadece hasNewMessage özelliğini true yap
                        return prevSessions.map(existingSession =>
                            existingSession.sessionId === newSession.sessionId
                                ? { ...existingSession, hasNewMessage: true }
                                : existingSession
                        );
                    } else {
                        // Yoksa yeni oturumu ekle
                        return [...prevSessions, newSession];
                    }
                });


                playBeepSound(); // Yeni oturum için ses çal
            });

            // Müşteri tarafından yeni mesaj geldiğinde çağrılır
            newConnection.on('receiveMessageByClientUser', (message: SendMessageByClientUserResponse) => {
                if (message.sessionId == GlobalOperations.ActiveSessionId) {
                    // Aktif oturuma gelen mesajı ekle
                    const newMessage: AgentSideMessages = {
                        messageContent: message.clientUserMessage,
                        messageOwnerName: message.clientUserName,
                        messageDateUtc: message.messageDateUtc,
                        messageSide: MessageSideEnum.ClientUser
                    };
                    addMessage(newMessage);
                } else {
                    // Oturum mesajı başka bir oturumdaysa yeni mesaj olduğunu belirt
                    setActiveSessions(prevSessions =>
                        prevSessions.map(session =>
                            session.sessionId === message.sessionId
                                ? { ...session, hasNewMessage: true }
                                : session
                        )
                    );


                }
            });

            newConnection.on('endSession4Agent', (session: EndSessionByAgentResponse) => {
                // Gelen oturum ID'si ile aktif oturum ID'si eşleşiyor mu kontrol edin
                if (session.sessionId === GlobalOperations.ActiveSessionId) {
                    // Oturumu yan paneldeki listeden kaldırmak için removeSessionById fonksiyonunu çağırın
                    removeSessionById(session.sessionId);

                    // Aktif oturumu sıfırlayın, çünkü bu oturum sonlandı
                    GlobalOperations.ActiveSessionId = 0;

                    // Mesaj listesini temizleyin
                    setMessages([]);
                } else {
                    // Eğer aktif oturum değilse, sadece yan paneldeki listeden oturumu kaldırın
                    removeSessionById(session.sessionId);
                }
            });

            newConnection.on('changeAgentTyping4Agent', (session: ChangeAgentTypingResponse) => {

                setTypingUsersState(prevState => {
                    const currentTypingUsers = prevState[session.sessionId] || [];
                    if (session.isTyping) {
                        // Eğer kullanıcı yazıyorsa ve listede yoksa ekle
                        if (!currentTypingUsers.includes(session.agentName)) {
                            return { ...prevState, [session.sessionId]: [...currentTypingUsers, session.agentName] };
                        }
                    } else {
                        // Eğer kullanıcı yazmayı bıraktıysa listeden çıkar
                        return {
                            ...prevState,
                            [session.sessionId]: currentTypingUsers.filter(user => user !== session.agentName),
                        };
                    }
                    return prevState;
                });
            });


        } catch (error) {
            console.error("SignalR başlatma hatası:", error);
        }
    };

    // Beep sesi çalma fonksiyonu
    const playBeepSound = () => {
        const beepSound = new Audio("data:audio/wav;base64,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");
        beepSound.play().catch(error => console.error("Ses çalma hatası:", error));
    };


    return (
        <ChatContext.Provider value={{
            messages,
            setMessages,
            addMessage,
            startSignalR,
            activeSessions,
            setActiveSessions,
            currentSession,
            setCurrentSession,
            removeSessionById,
            typingUsers,
            setTypingUsers,
            checkBeforeSwitchingSession,
            setGlobalInputValue
        }}>
            {children}
        </ChatContext.Provider>
    );
};

// ChatContext kullanımı
export const useChat = () => {
    const context = useContext(ChatContext);
    if (context === undefined) {
        throw new Error('useChat must be used within a ChatProvider');
    }
    return context;
};
